import React, { useEffect, useMemo } from 'react'
import styled, { keyframes } from 'styled-components'
import Link, { LinkProps } from '../link'
import UpIcon from '~/components/icons/arrows/16px_minimal-up.svg'
import DownIcon from '~/components/icons/arrows/16px_minimal-down.svg'
import useItems from '../../hooks/items'
import {
  ascending,
  boolean,
  entity,
  equals,
  isNull,
  max,
  min,
  notNull,
  query,
  some,
  string,
  truthy,
} from '~/prix'
import { institutionalColors } from '~/design'
import { useParams } from 'react-router-dom'
import SearchField from '../form/inputs/searchField'
import Badge from '../badge'
import { alertColors, alertLabels } from '~/packages/timeSeries/timeSeries.data'
import { format } from 'date-fns'
import PulseLoaderComponent from '../pulseLoader'
import SkeletonLoader from '../skeletonLoader'

const xPadding = '1rem'
const yPadding = '1.2rem'

const Wrapper = styled(Link)<{ $beforeWidth?: string; $beforeLeft?: string }>`
  display: flex;
  flex-direction: row;
  background-color: ${({ isActive }) => (isActive ? 'rgba(0,0,0,0.15)' : 'transparent')};
  color: ${({ theme }) => theme.colors.darkText};
  padding: ${yPadding} ${xPadding};
  position: relative;
  font-weight: 600;
  transition: font-weight 0.2s ease-in-out;

  &.dropdown-item {
    background-color: ${({ isActive }) => (isActive ? 'rgba(0,0,0,0.15)' : 'transparent')};
    color: ${({ isActive, theme }) => (isActive ? '#000' : theme.colors.darkText)};
  }

  &:not(:last-of-type)::before {
    content: '';
    display: block;
    position: absolute;
    bottom: 0;
    width: ${props => props.$beforeWidth || '100%'};
    left: ${props => props.$beforeLeft || '0'};
    border-bottom: 1px solid ${({ isActive }) => (!isActive ? 'rgba(0,0,0,0.1)' : 'transparent')};
  }

  &:hover {
    background-color: rgba(0, 0, 0, 0.1);

    &:not(:last-of-type)::before {
      border-bottom-color: transparent;
    }

    .left span {
      color: #000;
      font-weight: 600;
      transition: font-weight 0.2s ease-in-out;
    }
  }

  .left {
    display: flex;
    flex: 1;
    flex-direction: column;

    p {
      margin: 0;
    }
  }

  .right {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    padding-left: ${xPadding};
    align-self: center;
  }

  .description {
    font-size: 0.8rem;
    color: ${institutionalColors.graySebrae45};
    margin: 0;
    min-height: 14px;
  }
`

const DropdownContainer = styled.div`
  display: flex;
  flex-direction: column;
  text-align: left;
  color: ${institutionalColors.graySebrae30};
  background: #fff;

  padding: 12px 8px;
  border-bottom: solid 1px rgba(221, 221, 221, 1);
  min-height: 375px;
  overflow: hidden;
`

const ScrollableWrapper = styled.div`
  overflow-y: scroll;
  min-height: 322px;
  padding-bottom: 12px;
`

export interface MenuItemCardProps extends LinkProps {
  timeSeriesKey?: string
  title: string
  label?: string
  childrenOnRight?: React.ReactNode
  setSelectedSasCode?: React.Dispatch<React.SetStateAction<string | null>>
  menuVisibility?: (visibility: boolean) => void
}

const normalizeText = (text: string) => text.normalize('NFD').replace(/[\u0300-\u036f]/g, '')

export default function MenuItemCard({
  timeSeriesKey,
  title,
  label,
  childrenOnRight,
  children,
  menuVisibility,
  setSelectedSasCode,
  ...linkProps
}: MenuItemCardProps) {
  const isDropdown = timeSeriesKey === 'legalEntityIndividualPortfolioCourses'
  const [dropdownVisibility, setDropdownVisibility] = React.useState(false)
  const [searchTerm, setSearchTerm] = React.useState('')
  const params = useParams()

  const timeSeriesResult = isDropdown
    ? useItems(
        () =>
          query('courseProduct')
            .select({
              sasCode: entity('courseProduct').property('portfolioSasCode'),
              courseName: entity('courseProduct').property('productName'),
              alertLevel: entity('timeSeries').property('mostRecentAlert'),
            })
            .join({
              current: entity('courseProduct').property('portfolioSasCode'),
              target: entity('timeSeries').property('sasCode'),
              join: 'inner',
            })
            .where(
              ...[
                equals(
                  entity('courseProduct').property('isActivePortfolio'),
                  boolean().value(true),
                ),
                notNull(entity('timeSeries').property('components')),
                equals(
                  entity('timeSeries').property('key'),
                  string().value('legalEntityIndividualPortfolioCourses'),
                ),
                isNull(entity('timeSeries').property('predictedValue')),
              ].filter(truthy),
            )
            .order(ascending('courseName')),
        [timeSeriesKey],
      )
    : undefined

  const dateRangeTimeSeriesResult = timeSeriesKey
    ? useItems(
        () =>
          query('timeSeries')
            .select({
              minDate: min(entity('timeSeries').property('date')),
              maxDate: max(entity('timeSeries').property('date')),
              sasCode: entity('timeSeries').property('sasCode'),
            })
            .where(
              ...[
                equals(entity('timeSeries').property('key'), string().value(timeSeriesKey)),
                isNull(entity('timeSeries').property('predictedValue')),
              ].filter(truthy),
            ),
        [timeSeriesKey],
        {
          cache: 1000 * 60 * 60 * 8,
          autoLoad: Boolean(timeSeriesKey),
        },
      )
    : null

  const combinedItems: Record<string, { minDate: string; maxDate: string }> = useMemo(() => {
    if (!timeSeriesResult?.items || !dateRangeTimeSeriesResult?.items) {
      return {}
    }

    return dateRangeTimeSeriesResult.items.reduce(
      (acc, item) => {
        acc[String(item.sasCode)] = {
          minDate: format(new Date(item.minDate), 'dd/MM/yyyy'),
          maxDate: format(new Date(item.maxDate), 'dd/MM/yyyy'),
        }
        return acc
      },
      {} as Record<string, { minDate: string; maxDate: string }>,
    )
  }, [timeSeriesResult?.items, dateRangeTimeSeriesResult?.items])

  const { minDate, maxDate } = useMemo(() => {
    if (!dateRangeTimeSeriesResult?.isLoading && dateRangeTimeSeriesResult?.items) {
      const item = dateRangeTimeSeriesResult.items[0]
      return {
        minDate: format(new Date(item.minDate), 'dd/MM/yyyy'),
        maxDate: format(new Date(item.maxDate), 'dd/MM/yyyy'),
      }
    }

    return { minDate: null, maxDate: null }
  }, [dateRangeTimeSeriesResult?.items])

  const handleClick = () => {
    setDropdownVisibility(!dropdownVisibility)
  }

  const handleSelect = (sasCode: string) => {
    setSelectedSasCode && setSelectedSasCode(sasCode)
  }

  useEffect(() => {
    if (params.sasCode) {
      setDropdownVisibility(true)
    }
  }, [params.sasCode])

  // Dropdown para os cursos individuais
  if (isDropdown) {
    const filteredItems =
      timeSeriesResult?.items && !timeSeriesResult.isLoading
        ? timeSeriesResult.items.filter(item =>
            normalizeText(String(item.courseName))
              .toLowerCase()
              .includes(normalizeText(searchTerm).toLowerCase()),
          )
        : undefined

    return (
      <>
        <Wrapper
          onClick={handleClick}
          style={{ flexDirection: 'column', borderBottom: 'solid 1px rgba(221, 221, 221, 1)' }}
          isActive={linkProps.isActive}
        >
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-between',
            }}
          >
            <div className='left'>
              {label && <small>{label}</small>}
              <strong>{title}</strong>
            </div>

            <div className='right'>
              {dropdownVisibility ? (
                <UpIcon width={10} height={10} />
              ) : (
                <DownIcon width={10} height={10} />
              )}
            </div>
          </div>
        </Wrapper>

        {dropdownVisibility && (
          <DropdownContainer>
            <SearchField value={searchTerm} onChange={setSearchTerm} />
            {timeSeriesResult?.isLoading ? (
              <PulseLoaderComponent />
            ) : filteredItems && filteredItems.length > 0 ? (
              <ScrollableWrapper>
                {filteredItems.map((item, index) => (
                  <Wrapper
                    style={{ margin: '0 10px', borderRadius: '5px', minHeight: 'initial' }}
                    {...linkProps}
                    className='dropdown-item'
                    key={index}
                    onClick={() => handleSelect(String(item.sasCode))}
                    href={`/app/time-series/${timeSeriesKey}/sas/${item.sasCode}`}
                    isActive={params.sasCode === String(item.sasCode)}
                    $beforeLeft='1%'
                    $beforeWidth='98%'
                  >
                    <div className='left'>
                      <span>{String(item.courseName)}</span>
                      {combinedItems[item.sasCode as string] ? (
                        <p className='description'>
                          Dados entre {combinedItems[item.sasCode as string].minDate} -{' '}
                          {combinedItems[item.sasCode as string].maxDate}
                        </p>
                      ) : (
                        <SkeletonLoader $width='200px' $height='14px' />
                      )}
                    </div>

                    {item.alertLevel && item.alertLevel !== 'informative' ? (
                      <div className='right'>
                        <Badge
                          background={alertColors[item.alertLevel as keyof typeof alertColors]}
                        >
                          {alertLabels[item.alertLevel as keyof typeof alertColors]}
                        </Badge>
                      </div>
                    ) : null}
                  </Wrapper>
                ))}
              </ScrollableWrapper>
            ) : (
              <Wrapper>Nenhum curso encontrado</Wrapper>
            )}
          </DropdownContainer>
        )}
      </>
    )
  }

  return (
    <Wrapper {...linkProps} onClick={() => menuVisibility?.(false)}>
      <div className='left'>
        {label && <small>{label}</small>}
        <strong>{title}</strong>
        {timeSeriesKey ? (
          dateRangeTimeSeriesResult?.isLoading ? (
            <SkeletonLoader $width='200px' $height='14px' />
          ) : (
            <p className='description'>
              {minDate && maxDate ? `Dados entre ${minDate} - ${maxDate}` : null}
            </p>
          )
        ) : null}
      </div>
      {childrenOnRight ? <div className='right'>{childrenOnRight}</div> : null}
    </Wrapper>
  )
}
